import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Options } from '../../libs';

interface onSearch<V> {
  (searchText: V, selected: true): void;
  (searchText: string, selected: false): void;
}

interface SearchBarProps<T, V> {
  onSearch: onSearch<V>;
  getOptionValue: (value: Options<T> | null) => V;
  placeholder?: string;
  options: Options<T>[];
  searchLoading: boolean;
  debounce: boolean;
}

function SearchBar<T, V>({
  searchLoading,
  placeholder = 'Search...',
  onSearch,
  getOptionValue,
  options,
  debounce
}: SearchBarProps<T, V>) {
  const [selectedOption, setSelectedOption] = useState<Options<T> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const debounced = useDebouncedCallback(
    async (value) => {
      await handleOnInputChange(value);
    },
    debounce ? 600 : 0
  );

  // eslint-disable-next-line @typescript-eslint/require-await -- keeps an onSearch throw as a rejection of the awaited call
  const handleOnInputChange = async (value: string) => {
    onSearch(value, false);
  };

  return (
    <Autocomplete
      id="search-id"
      value={selectedOption}
      inputValue={searchTerm}
      loading={searchLoading}
      options={!searchLoading && searchTerm ? options : []}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onInputChange={(event, val) => {
        if (event?.type === 'change') {
          setSearchTerm(val);
          void debounced(val);
        }
      }}
      onChange={(_, val) => {
        setSelectedOption(val);
        setSearchTerm(val?.label ?? '');

        onSearch(getOptionValue(val), true);
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                style: {
                  fontSize: '17px',
                  fontWeight: 400,
                  borderRadius: '8px'
                }
              },
              htmlInput: {
                ...params.slotProps.htmlInput,
                className: '',
                style: {
                  fontSize: '17px',
                  fontWeight: 400
                }
              }
            }}
          />
        );
      }}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
    />
  );
}

export default SearchBar;
